/*
 * Copyright (c) 2021 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { DashBoardFinState } from '../../../data/reducers/dashBoardFinReducer';
import { DashBoardTechState } from '../../../data/reducers/dashBoardTechReducer';

export enum Direction {
    HORIZONTAL,
    VERTICAL,
}

export enum LabelType {
    EXTENDED,
    SHORT,
    NONE,
}

export interface SeriesItemConfig<T extends object> {
    keyForValue: keyof T;
    color: string;
    translatePath: string;
}

export function getTotalByConfig<T extends object, U extends SeriesItemConfig<T>>(data: T, config: U[]): number {
    return config.reduce((total, item) => {
        const dataType = typeof data[item.keyForValue];
        if (dataType === 'number' || dataType === 'string') total += Number(data[item.keyForValue]);
        return total;
    }, 0);
}

export enum FinChartType {
    PAYMENT_TYPES = 'paymentTypesStat',
    TURNOVER_BY_ACTIVITY = 'turnoverByActivity',
    COVERAGE = 'coverageByTaxationTypes',
}

export enum TechChartType {
    DOCUMENTS = 'documentTypesStat',
    DOCUMENTS_COUNTER = 'documentsCounter',
    CASHBOXES = 'cashBoxesStat',
    OPERATORS = 'operatorsCashBoxesStat',
    TOKENS = 'deviceStatusesStat',
}

export type FinChartsIsLoadingKeys = Pick<
    DashBoardFinState,
    'isLoadingPaymentTypes' | 'isLoadingTurnoversByActivities' | 'isLoadingRegistryItemsCountInfo'
>;

export type TechChartsIsLoadingKeys = Pick<
    DashBoardTechState,
    | 'isLoadingDocsCountsStat'
    | 'isLoadingOperatorsCashBoxesStat'
    | 'isLoadingCashBoxesStat'
    | 'isLoadingDocData'
    | 'isLoadingDeviceCountsStat'
>;

export interface FileMapItemType<T extends object> {
    fileName: string;
    isLoadingKey: keyof T;
    endpoint: string;
}

export const FinChartsFilesMap: { [key in FinChartType]: FileMapItemType<FinChartsIsLoadingKeys> } = {
    [FinChartType.PAYMENT_TYPES]: {
        fileName: FinChartType.PAYMENT_TYPES + '.xlsx',
        isLoadingKey: 'isLoadingPaymentTypes',
        endpoint: `1.0.0/infographics/${FinChartType.PAYMENT_TYPES}/excel`,
    },
    [FinChartType.TURNOVER_BY_ACTIVITY]: {
        fileName: FinChartType.TURNOVER_BY_ACTIVITY + '.xlsx',
        isLoadingKey: 'isLoadingTurnoversByActivities',
        endpoint: `1.0.0/infographics/${FinChartType.TURNOVER_BY_ACTIVITY}/excel`,
    },
    [FinChartType.COVERAGE]: {
        fileName: FinChartType.COVERAGE + '.xlsx',
        isLoadingKey: 'isLoadingRegistryItemsCountInfo',
        endpoint: `1.0.0/infographics/${FinChartType.COVERAGE}/excel`,
    },
};

export const TechChartsFilesMap: { [key in TechChartType]: FileMapItemType<TechChartsIsLoadingKeys> } = {
    [TechChartType.DOCUMENTS]: {
        fileName: 'receiptTypesStat.xlsx',
        isLoadingKey: 'isLoadingDocData',
        endpoint: `1.0.0/infographics/${TechChartType.DOCUMENTS}/excel`,
    },
    [TechChartType.DOCUMENTS_COUNTER]: {
        fileName: 'receiptsCounter.xlsx',
        isLoadingKey: 'isLoadingDocsCountsStat',
        endpoint: `1.0.0/infographics/${TechChartType.DOCUMENTS_COUNTER}/excel`,
    },
    [TechChartType.CASHBOXES]: {
        fileName: TechChartType.CASHBOXES + '.xlsx',
        isLoadingKey: 'isLoadingCashBoxesStat',
        endpoint: `1.0.0/infographics/${TechChartType.CASHBOXES}/excel`,
    },
    [TechChartType.OPERATORS]: {
        fileName: TechChartType.OPERATORS + '.xlsx',
        isLoadingKey: 'isLoadingOperatorsCashBoxesStat',
        endpoint: `1.0.0/infographics/${TechChartType.OPERATORS}/excel`,
    },
    [TechChartType.TOKENS]: {
        fileName: 'tokensStatusesStat.xlsx',
        isLoadingKey: 'isLoadingDeviceCountsStat',
        endpoint: `1.0.0/infographics/${TechChartType.TOKENS}/excel`,
    },
};
